export const ZONE_GRID_COLUMNS = 8;
export const ZONE_GRID_MAX_ROWS = 4;
export const ZONE_GRID_MAX_CELLS = ZONE_GRID_COLUMNS * ZONE_GRID_MAX_ROWS;

export const parseZoneTotal = (total) => {
  const [current = '0', max = '0'] = String(total || '0/0').split('/');
  return {
    current: current.trim(),
    max: max.trim(),
  };
};

export const isZoneTotalCountAlert = (zone) => {
  const { current, max } = parseZoneTotal(zone?.total);
  return current !== max;
};

export const parseProductSoldQty = (product) => {
  const value = product?.sold_qty ?? product?.sold ?? product?.Sold;
  if (value === null || value === undefined) return 0;
  const str = String(value).trim();
  if (str === '' || str === '-') return 0;
  const num = Number(str);
  return Number.isFinite(num) ? num : 0;
};

export const productHasSoldQty = (product) => parseProductSoldQty(product) > 0;

export const parseRefillSoldStatus = (product) => {
  const value = product?.refillSoldStatus ?? product?.refill_sold_status;
  if (value === null || value === undefined) return null;
  if (value === true || value === 'true' || value === 1 || value === '1') {
    return true;
  }
  if (value === false || value === 'false' || value === 0 || value === '0') {
    return false;
  }
  return null;
};

export const productIsSummarySoldItem = (product) =>
  productHasSoldQty(product) && parseRefillSoldStatus(product) === false;

export const getSummarySoldProducts = (zone) =>
  (zone?.products || []).filter(productIsSummarySoldItem);

export const zoneHasSummarySoldItems = (zone) =>
  getSummarySoldProducts(zone).length > 0;

export const getZoneOpenIssueId = (zone) => {
  const issueId =
    zone?.open_issue_id ?? zone?.issue_id ?? zone?.openIssueId ?? null;
  if (issueId === null || issueId === undefined || issueId === '') return null;
  return String(issueId);
};

export const getZoneAiChatContext = (zone, location) => {
  const zoneId = zone?.zone_record_id ?? null;
  const locationId =
    location?.location_id ?? location?._id ?? location?.id ?? null;
  const issueId = getZoneOpenIssueId(zone);

  if (!zoneId || !locationId || !issueId) return null;

  return {
    zoneId: String(zoneId),
    locationId: String(locationId),
    issueId: String(issueId),
  };
};

export const zoneCanUseAiSuggest = (zone, location) =>
  zoneHasSummarySoldItems(zone) &&
  Boolean(getZoneAiChatContext(zone, location));

export const getSummaryZoneRowKey = (zone, location) =>
  String(
    zone?.zone_record_id ||
      `${location?.location_id || 'location'}-${zone?.zone_id || zone?._id || 'zone'}`
  );

export const zoneHasSoldProducts = (zone) =>
  (zone?.products || []).some(productHasSoldQty);

export const zoneHasProducts = (zone) => (zone?.products?.length ?? 0) > 0;

export const sortZonesWithSoldFirst = (zones = []) =>
  [...zones].sort((zoneA, zoneB) => {
    const soldA = zoneHasSoldProducts(zoneA) ? 0 : 1;
    const soldB = zoneHasSoldProducts(zoneB) ? 0 : 1;
    return soldA - soldB;
  });

export const getLocationId = (location) =>
  String(location?.location_id ?? location?._id ?? location?.id ?? '');

export const getZoneDisplayName = (zone) =>
  String(zone?.zone ?? zone?.zone_name ?? zone?.name ?? '').trim();

export const filterLocationsByGridSearch = (locations = [], searchQuery = '') => {
  const query = String(searchQuery || '').trim().toLowerCase();
  if (!query) return locations;

  return (locations || [])
    .map((location) => {
      const locationName = String(location?.name ?? '').toLowerCase();
      const locationMatches = locationName.includes(query);

      if (locationMatches) {
        return location;
      }

      const matchingZones = (location?.zones || []).filter((zone) =>
        getZoneDisplayName(zone).toLowerCase().includes(query)
      );

      if (!matchingZones.length) {
        return null;
      }

      return { ...location, zones: matchingZones };
    })
    .filter(Boolean);
};

export const filterLocationsForZoneIndex = (
  locations = [],
  filters = {}
) => {
  const locationIds = filters?.locations || [];
  const soldOut = filters?.soldOut || [];
  let result = locations || [];

  if (locationIds.length > 0) {
    const idSet = new Set(locationIds.map(String));
    result = result.filter((location) => idSet.has(getLocationId(location)));
  }

  if (soldOut.includes('yes')) {
    result = result
      .map((location) => ({
        ...location,
        zones: (location?.zones || []).filter(zoneHasSoldProducts),
      }))
      .filter((location) => (location?.zones || []).length > 0);
  }

  return result;
};

export const vaultRowHasSoldOut = (row) => {
  const sold = row?.sold ?? row?.Sold ?? 0;
  if (sold === null || sold === undefined || sold === '-') return false;
  const num = Number(sold);
  return Number.isFinite(num) && num > 0;
};

export const filterVaultListForZoneIndex = (
  rows = [],
  filters = {},
  locationOptions = []
) => {
  const locationIds = filters?.locations || [];
  const soldOut = filters?.soldOut || [];
  let result = rows || [];

  if (locationIds.length > 0) {
    const idSet = new Set(locationIds.map(String));
    const selectedNames = new Set(
      locationOptions
        .filter((option) => idSet.has(option.id))
        .map((option) => option.name)
    );

    result = result.filter((row) => {
      const rowLocationIds = [
        row?.location_id,
        row?.LocationId,
        row?.locationId,
      ]
        .filter((value) => value !== null && value !== undefined && value !== '')
        .map(String);

      if (rowLocationIds.some((id) => idSet.has(id))) {
        return true;
      }

      const rowLocationName = String(
        row?.location_name ?? row?.LocationName ?? row?.LocationId ?? ''
      );
      return selectedNames.has(rowLocationName);
    });
  }

  if (soldOut.includes('yes')) {
    result = result.filter(vaultRowHasSoldOut);
  }

  return result;
};

export const getLocationsWithZones = (locations = []) =>
  (locations || [])
    .map((location) => ({
      ...location,
      zones: sortZonesWithSoldFirst(
        (location?.zones || []).filter(zoneHasProducts)
      ),
    }))
    .filter((location) => location.zones.length > 0);

export const shouldCenterZoneProducts = (productCount = 0) => {
  const count = Math.max(0, productCount);
  return count > 0 && count <= 4;
};

export const getZoneGridColumns = (productCount = 0) => {
  const count = Math.max(0, productCount);
  if (count <= 8) return 4;
  return ZONE_GRID_COLUMNS;
};

export const buildZoneProductGridRows = (
  products = [],
  columns = ZONE_GRID_COLUMNS
) => {
  const items = products || [];
  const columnCount = Math.max(1, columns);
  let cells;

  if (items.length <= ZONE_GRID_MAX_CELLS) {
    cells = items.map((product) => ({ kind: 'product', product }));
  } else {
    const overflowCount = items.length - (ZONE_GRID_MAX_CELLS - 1);
    cells = [
      ...items.slice(0, ZONE_GRID_MAX_CELLS - 1).map((product) => ({
        kind: 'product',
        product,
      })),
      { kind: 'overflow', count: overflowCount },
    ];
  }

  const rows = [];
  for (let index = 0; index < cells.length; index += columnCount) {
    rows.push(cells.slice(index, index + columnCount));
  }

  return rows;
};
